import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { listTrees, TreeSummary } from "../../api/trees";
import { useDeleteTree } from "../../hooks/useTreeSharing";
import { useVaultContext } from "../../context/VaultContext";
import type { ApiError } from "../../types";
import LoadingSpinner from "../Common/LoadingSpinner";
import TreeSharingModal from "./TreeSharingModal";

interface TreeListProps {
  onImportClick: () => void;
}

export default function TreeList({ onImportClick }: TreeListProps) {
  const { getAccessToken } = useAuth();
  const { currentVaultId } = useVaultContext();

  const {
    data: trees,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["trees", currentVaultId],
    queryFn: async () => {
      const token = await getAccessToken();
      return listTrees(token, currentVaultId || undefined);
    },
    enabled: !!currentVaultId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    const apiError = error as unknown as ApiError;
    const message =
      apiError?.detail || (error instanceof Error ? error.message : "Unknown error");
    const status = apiError?.status_code;
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        <p className="font-medium">Failed to load trees. Please try again.</p>
        <p className="mt-1 text-sm opacity-90">
          {message}
          {status != null && ` (${status})`}
        </p>
        {status === 401 && (
          <p className="mt-2 text-sm">
            Check Auth0 config and that backend AUTH0_DOMAIN/AUDIENCE match the frontend.
          </p>
        )}
        {message.toLowerCase().includes("consent required") && (
          <p className="mt-2 text-sm">
            Refreshing the page should open a popup to grant access—allow popups for this site if it
            doesn’t.
          </p>
        )}
      </div>
    );
  }

  if (!trees || trees.length === 0) {
    return (
      <div className="rounded-lg border-2 border-dashed border-vault-300 p-8 text-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="mx-auto h-12 w-12 text-vault-400"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
          />
        </svg>
        <h3 className="mt-4 font-serif text-lg font-semibold text-vault-900">
          No family trees yet
        </h3>
        <p className="mt-2 text-vault-600">
          Import a GEDCOM file to create your first family tree.
        </p>
        <button
          onClick={onImportClick}
          className="btn-primary mt-4"
        >
          Import GEDCOM
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl font-semibold text-vault-900">
          Your Family Trees
        </h2>
        <button onClick={onImportClick} className="btn-primary">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="mr-2 h-5 w-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4.5v15m7.5-7.5h-15"
            />
          </svg>
          Import GEDCOM
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {trees.map((tree) => (
          <TreeCard key={tree.id} tree={tree} />
        ))}
      </div>
    </div>
  );
}

function TreeCard({ tree }: { tree: TreeSummary }) {
  const [showSharing, setShowSharing] = useState(false);
  const { currentVault } = useVaultContext();
  const deleteMutation = useDeleteTree();
  const updatedDate = new Date(tree.updated_at).toLocaleDateString();
  
  // Check if user is admin (can delete trees)
  const isAdmin = currentVault && (
    currentVault.user_role === "VAULT_OWNER" || 
    currentVault.user_role === "VAULT_CO_ADMIN"
  );
  
  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete "${tree.name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await deleteMutation.mutateAsync(tree.id);
    } catch (error) {
      alert(error instanceof Error ? error.message : "Failed to delete tree");
    }
  };

  return (
    <>
      <div className="card transition-shadow hover:shadow-md">
        <div className="flex items-start justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-vault-100 text-vault-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
              />
            </svg>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-vault-100 px-2 py-1 text-xs font-medium text-vault-600">
              {tree.person_count} people
            </span>
            {isAdmin && (
              <>
                <button
                  onClick={() => setShowSharing(true)}
                  className="rounded-md p-1.5 text-vault-600 hover:bg-vault-100 transition-colors"
                  title="Share tree"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-4 w-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z"
                    />
                  </svg>
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="rounded-md p-1.5 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                  title="Delete tree"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-4 w-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                    />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

        <Link to={`/trees/${tree.id}`}>
          <h3 className="mt-3 font-serif text-lg font-semibold text-vault-900 hover:text-vault-700">
            {tree.name}
          </h3>
        </Link>

        {tree.description && (
          <p className="mt-1 text-sm text-vault-600 line-clamp-2">
            {tree.description}
          </p>
        )}

        <div className="mt-3 flex items-center text-xs text-vault-500">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="mr-1 h-4 w-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Updated {updatedDate}
        </div>

        {/* Quick action buttons */}
        <div className="mt-4 flex gap-2">
          <Link
            to={`/trees/${tree.id}`}
            className="flex-1 rounded-lg bg-vault-100 px-3 py-2 text-center text-sm font-medium text-vault-700 transition-colors hover:bg-vault-200"
          >
            View Tree
          </Link>
          <Link
            to={`/trees/${tree.id}/media`}
            className="flex-1 rounded-lg bg-amber-100 px-3 py-2 text-center text-sm font-medium text-amber-700 transition-colors hover:bg-amber-200"
          >
            Media Library
          </Link>
        </div>
      </div>

      {isAdmin && (
        <TreeSharingModal
          treeId={tree.id}
          treeName={tree.name}
          isOpen={showSharing}
          onClose={() => setShowSharing(false)}
        />
      )}
    </>
  );
}

